// src/components/GeminiRecommendations/GeminiRecommendations.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './GeminiRecommendations.css';

const GeminiRecommendations = ({ currentProduct, allProducts }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [geminiReason, setGeminiReason] = useState('');
  const navigate = useNavigate();

  const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

  useEffect(() => {
    if (currentProduct && allProducts) {
      generateGeminiRecommendations();
    }
  }, [currentProduct, allProducts]);

  const generateGeminiRecommendations = async () => {
    setIsLoading(true);

    try {
      const productContext = allProducts.map(p =>
        `ID:${p.id}|${p.name}|${p.category}|$${p.price}|${p.rating}⭐`
      ).join('\n');

      const prompt = `User is viewing: ${currentProduct.name} ($${currentProduct.price}) in ${currentProduct.category} category.

Available products:
${productContext}

Recommend 4 products that:
1. Complement the current product
2. Are in similar price range
3. Have high ratings
4. Create a cohesive shopping experience

Return JSON only:
{
  "recommendations": [product IDs],
  "reason": "Brief explanation why these products work well together"
}`;

      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-3-pro-latest:generateContent?key=${GEMINI_API_KEY}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: {
              temperature: 0.8,
              maxOutputTokens: 512,
              responseMimeType: 'application/json'
            }
          })
        }
      );

      if (!response.ok) throw new Error('Gemini API failed');

      const data = await response.json();
      const result = JSON.parse(data.candidates[0]?.content?.parts?.[0]?.text || '{}');

      const recommendedProducts = allProducts.filter(p =>
        result.recommendations?.includes(p.id) && p.id !== currentProduct.id
      ).slice(0, 4);

      setRecommendations(recommendedProducts);
      setGeminiReason(result.reason || 'Curated based on your interests');

    } catch (error) {
      console.error('Gemini recommendation error:', error);
      
      // Fallback recommendations
      const fallback = allProducts
        .filter(p => p.id !== currentProduct.id && p.category === currentProduct.category)
        .slice(0, 4);
      
      setRecommendations(fallback);
      setGeminiReason('Products from the same category');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="gemini-recommendations-sidebar">
        <div className="gemini-rec-header">
          <h3>🤖 Gemini Recommendations</h3>
        </div>
        <div className="gemini-rec-loading">
          <div className="gemini-rec-spinner"></div>
          <p>Analyzing...</p>
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) return null;

  return (
    <div className="gemini-recommendations-sidebar">
      <div className="gemini-rec-header">
        <h3>🤖 Recommended for You</h3>
        <span className="gemini-rec-badge">Gemini 3 AI</span>
      </div>

      <div className="gemini-rec-reason">
        <p>💡 {geminiReason}</p>
      </div>

      <div className="gemini-rec-products">
        {recommendations.map(product => (
          <div
            key={product.id}
            className="gemini-rec-card"
            onClick={() => navigate(`/product/${product.id}`)}
          >
            <div className="gemini-rec-image">
              {product.modelSrc ? (
                <model-viewer
                  src={product.modelSrc}
                  ios-src={product.iOSSrc}
                  alt={product.name}
                  camera-controls
                  disable-zoom
                  style={{ width: '100%', height: '100%' }}
                />
              ) : (
                <div className="gemini-rec-placeholder">📦</div>
              )}
            </div>
            <div className="gemini-rec-info">
              <h4>{product.name}</h4>
              <div className="gemini-rec-meta">
                <span className="gemini-rec-price">${product.price}</span>
                <span className="gemini-rec-rating">⭐ {product.rating || '4.5'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GeminiRecommendations;
